import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { logError } from "@/lib/logger";
import { Plus, X, Buildings, Trash, PencilSimple, Briefcase, ArrowSquareOut, LockKey } from "@phosphor-icons/react";
import AutocompleteInput from "@/components/AutocompleteInput";
import { LOCATION_SUGGESTIONS, INDUSTRY_SUGGESTIONS, INDUSTRY_TYPE_OPTIONS } from "@/lib/suggestions";
import { useAuth } from "@/context/AuthContext";
import { useFeatures } from "@/lib/features";
import ClientLocationsSection from "@/components/ClientLocationsSection";

const empty = {
    name: "",
    industry_type: "",
    industry: "",
    website: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    location: "",
    notes: "",
    status: "active",
};

export default function Clients() {
    const { user } = useAuth();
    const { isEnabled } = useFeatures();
    // P0 RBAC — Recruiters can no longer create/edit/delete clients.
    const canEditClient = user?.role === "admin" || user?.role === "hiring_manager";
    const [clients, setClients] = useState([]);
    const [showForm, setShowForm] = useState(false);
    const [form, setForm] = useState(empty);
    const [editing, setEditing] = useState(null);
    const [err, setErr] = useState("");
    const [saving, setSaving] = useState(false);
    // V1.5.5.6 — When routed here from JobDetail's "Manage on client →" link
    // (URL: /clients?edit=<cid>#locations) auto-open the edit form for that
    // client and scroll to the Locations section so the recruiter can add or
    // fix SPOCs without hunting for the row.
    const [searchParams, setSearchParams] = useSearchParams();
    const editRequestId = searchParams.get("edit");
    const scrollToLocations = typeof window !== "undefined" && window.location.hash === "#locations";
    const locationsRef = useRef(null);
    const autoOpenedRef = useRef(false);

    const load = useCallback(
        () =>
            api
                .get("/clients")
                .then((r) => setClients(r.data))
                .catch((e) => logError("Failed to load clients:", e)),
        [],
    );

    useEffect(() => {
        load();
    }, [load]);

    // Auto-open edit form when arriving with ?edit=<cid>
    useEffect(() => {
        if (autoOpenedRef.current) return;
        if (!editRequestId || !clients.length) return;
        const match = clients.find((c) => c.id === editRequestId);
        if (!match) return;
        autoOpenedRef.current = true;
        setEditing(match.id);
        setForm({ ...empty, ...match });
        setShowForm(true);
        // Clear the ?edit= param so refresh doesn't loop.
        const next = new URLSearchParams(searchParams);
        next.delete("edit");
        setSearchParams(next, { replace: true });
    }, [editRequestId, clients, searchParams, setSearchParams]);

    // After the edit form mounts, scroll to the Locations section if #locations was requested.
    useEffect(() => {
        if (!showForm || !scrollToLocations) return;
        // Give the form a tick to layout before scrolling.
        const t = setTimeout(() => {
            locationsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 200);
        return () => clearTimeout(t);
    }, [showForm, scrollToLocations]);

    const submit = async (e) => {
        e.preventDefault();
        setErr("");
        setSaving(true);
        try {
            if (editing) await api.put(`/clients/${editing}`, form);
            else await api.post("/clients", form);
            setForm(empty);
            setEditing(null);
            setShowForm(false);
            load();
        } catch (ex) {
            setErr(formatApiError(ex.response?.data?.detail) || ex.message);
        } finally {
            setSaving(false);
        }
    };

    const startEdit = (c) => {
        setEditing(c.id);
        setForm({ ...empty, ...c });
        setShowForm(true);
    };

    const remove = async (c) => {
        if (!window.confirm(`Delete ${c.name}? Jobs will be unlinked but not deleted.`)) return;
        try {
            await api.delete(`/clients/${c.id}`);
            load();
        } catch (ex) {
            alert(formatApiError(ex.response?.data?.detail) || ex.message);
        }
    };

    return (
        <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-[1600px] w-full min-w-0 mx-auto" data-testid="clients-page" style={{ maxWidth: "min(1600px, 100vw)" }}>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 sm:mb-10">
                <div className="min-w-0 flex-1">
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#A3A3A3] mb-2">
                        Companies you recruit for
                    </p>
                    <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-ink break-words">
                        Clients.
                    </h1>
                </div>
                {canEditClient && (
                    <button
                        onClick={() => {
                            setEditing(null);
                            setForm(empty);
                            setShowForm(true);
                        }}
                        data-testid="open-new-client-button"
                        className="self-start md:self-auto shrink-0 bg-cobalt hover:bg-cobalt-dark text-white px-5 py-2.5 sm:px-6 sm:py-3 font-bold tracking-wide text-sm flex items-center gap-2 transition-colors"
                    >
                        <Plus size={14} weight="bold" /> Add client
                    </button>
                )}
            </div>

            {clients.length === 0 ? (
                <div className="bg-white border border-[#E5E5E5] p-16 text-center">
                    <Buildings size={36} weight="bold" className="mx-auto text-[#A3A3A3] mb-4" />
                    <p className="font-heading text-2xl font-bold tracking-tight mb-2">
                        No clients yet
                    </p>
                    <p className="text-[#525252] mb-6">
                        Add the first company you recruit for to start tagging requirements.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {clients.map((c) => (
                        <div
                            key={c.id}
                            data-testid={`client-card-${c.id}`}
                            className="bg-white border border-[#E5E5E5] hover:border-ink p-6 flex flex-col gap-3 transition-colors"
                        >
                            <div className="flex items-start justify-between">
                                <div className="min-w-0">
                                    <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">
                                        {c.industry_type || c.industry || "—"}
                                    </p>
                                    <h3 className="font-heading text-xl font-bold tracking-tight text-ink truncate">
                                        {c.name}
                                    </h3>
                                </div>
                                <span
                                    className={`text-[10px] font-black uppercase tracking-wider px-2 py-1 ${
                                        c.status === "active"
                                            ? "bg-[#ECFDF5] text-[#059669]"
                                            : "bg-[#F5F5F5] text-[#525252]"
                                    }`}
                                >
                                    {c.status}
                                </span>
                            </div>
                            {c.contact_name && (
                                <p className="text-sm text-[#525252]">
                                    {c.contact_name} · {c.contact_email}
                                </p>
                            )}
                            {c.slug && (
                                isEnabled("public_career_page") ? (
                                    <a
                                        href={`/careers/${c.slug}`}
                                        target="_blank"
                                        rel="noreferrer"
                                        data-testid={`client-careers-link-${c.id}`}
                                        className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-cobalt hover:text-cobalt-dark self-start"
                                    >
                                        /careers/{c.slug}
                                        <ArrowSquareOut size={11} weight="bold" />
                                    </a>
                                ) : (
                                    <span
                                        data-testid={`client-careers-locked-${c.id}`}
                                        title="Public career pages are available on Pro and above"
                                        className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wider text-[#A3A3A3] self-start cursor-help"
                                    >
                                        <LockKey size={11} weight="fill" /> /careers/{c.slug} — upgrade
                                    </span>
                                )
                            )}
                            <div className="pt-3 border-t border-[#E5E5E5] flex items-center justify-between">
                                <span className="inline-flex items-center gap-1 text-xs font-bold text-[#525252]">
                                    <Briefcase size={12} weight="bold" />
                                    {c.open_job_count} open · {c.job_count} total
                                </span>
                                <div className="flex gap-2">
                                    {canEditClient && (
                                        <>
                                            <button
                                                onClick={() => startEdit(c)}
                                                data-testid={`edit-client-${c.id}`}
                                                className="p-1.5 border border-[#E5E5E5] hover:border-ink"
                                                aria-label="Edit"
                                            >
                                                <PencilSimple size={12} weight="bold" />
                                            </button>
                                            <button
                                                onClick={() => remove(c)}
                                                data-testid={`delete-client-${c.id}`}
                                                className="p-1.5 border border-[#E5E5E5] hover:border-[#DC2626] hover:text-[#DC2626]"
                                                aria-label="Delete"
                                            >
                                                <Trash size={12} weight="bold" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showForm && (
                <div
                    className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-40 flex items-center justify-center p-4"
                    onClick={() => setShowForm(false)}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        data-testid="client-form"
                        className="bg-white border border-[#E5E5E5] w-full max-w-xl max-h-[90vh] overflow-y-auto"
                    >
                        <div className="flex items-center justify-between p-6 border-b border-[#E5E5E5] sticky top-0 bg-white">
                            <h2 className="font-heading text-2xl font-bold tracking-tight">
                                {editing ? "Edit client" : "Add client"}
                            </h2>
                            <button
                                onClick={() => setShowForm(false)}
                                className="p-2 hover:bg-[#F5F5F5]"
                            >
                                <X size={18} weight="bold" />
                            </button>
                        </div>
                        <form onSubmit={submit} className="p-6 space-y-4">
                            {[
                                ["name", "Company name", true],
                                ["website", "Website", false],
                                ["contact_name", "Primary contact", false],
                                ["contact_email", "Contact email", false],
                                ["contact_phone", "Contact phone", false],
                            ].map(([k, label, req]) => (
                                <div key={k}>
                                    <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                        {label}
                                    </label>
                                    <input
                                        data-testid={`client-${k}-input`}
                                        type={k === "contact_email" ? "email" : k === "website" ? "url" : "text"}
                                        value={form[k] || ""}
                                        required={req}
                                        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                                        className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt"
                                    />
                                </div>
                            ))}
                            <div>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    Industry type
                                </label>
                                <select
                                    data-testid="client-industry-type-select"
                                    value={form.industry_type || ""}
                                    onChange={(e) => setForm({ ...form, industry_type: e.target.value })}
                                    className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 focus:outline-none focus:border-cobalt focus:ring-1 focus:ring-cobalt"
                                >
                                    <option value="">Select industry type…</option>
                                    {INDUSTRY_TYPE_OPTIONS.map((opt) => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                                <p className="text-[11px] text-[#A3A3A3] mt-1.5">
                                    Used to categorise the client on the public jobs board.
                                </p>
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    Sub-industry (optional)
                                </label>
                                <AutocompleteInput
                                    testId="client-industry-input"
                                    field="industry"
                                    staticOptions={INDUSTRY_SUGGESTIONS}
                                    value={form.industry || ""}
                                    onChange={(v) => setForm({ ...form, industry: v })}
                                    placeholder="e.g. SaaS, Fintech, Healthcare"
                                />
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    Head Office
                                </label>
                                <AutocompleteInput
                                    testId="client-location-input"
                                    field="location"
                                    staticOptions={LOCATION_SUGGESTIONS}
                                    value={form.location || ""}
                                    onChange={(v) => setForm({ ...form, location: v })}
                                    placeholder="e.g. Bangalore, Mumbai"
                                />
                                <p className="text-[10px] text-[#A3A3A3] mt-1">
                                    Short summary shown on client cards. Add full addresses in "Locations & SPOCs" below.
                                </p>
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    Status
                                </label>
                                <select
                                    data-testid="client-status-select"
                                    value={form.status}
                                    onChange={(e) => setForm({ ...form, status: e.target.value })}
                                    className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 focus:outline-none focus:border-cobalt"
                                >
                                    <option value="active">Active</option>
                                    <option value="archived">Archived</option>
                                </select>
                            </div>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#525252] mb-2 block">
                                    Notes
                                </label>
                                <textarea
                                    value={form.notes || ""}
                                    onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    rows={3}
                                    className="w-full bg-white border border-[#E5E5E5] px-4 py-2.5 focus:outline-none focus:border-cobalt"
                                />
                            </div>
                            {err && (
                                <div className="border border-[#DC2626] bg-[#FEF2F2] text-[#DC2626] text-sm px-4 py-3">
                                    {err}
                                </div>
                            )}

                            {/* V1.5.5 — Locations + SPOCs sub-section.
                                Only shown after the client is saved (needs a
                                client_id to attach locations against). */}
                            <div ref={locationsRef} id="locations">
                                <ClientLocationsSection clientId={typeof editing === "string" ? editing : editing?.id} />
                            </div>

                            <button
                                data-testid="client-submit-button"
                                disabled={saving}
                                type="submit"
                                className="w-full bg-cobalt hover:bg-cobalt-dark disabled:opacity-60 text-white px-6 py-3 font-bold tracking-wide text-sm"
                            >
                                {saving ? "Saving…" : editing ? "Save changes" : "Add client"}
                            </button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}
